import type { NarrationOptions, NarrationService } from '../../sdk/narration';
import type { AudioId } from '../../shared/types/ids';
import type { AudioPlaybackEnd } from '../audio/audio-service';
import { NARRATION_BOUNDS } from '../config/narration';
import { bakedNarrationAudioId, narrationLineKey } from './baked-line-id';

/**
 * Narration from recordings, with the browser voice underneath it (Slice A″, PS5-2/PS5-7).
 *
 * **A second implementation, not a second seam.** `NarrationService` has had a replaceable-engine
 * clause since Phase 1 (NAR-03) and nothing had ever exercised it. This is that exercise: the
 * mission runtime, the teaching controller and the calibration screen keep handing a sentence to
 * the same interface, and the choice of *how* it is said is made here.
 *
 * **The choice is per line, not per sitting.** A line the theme has a recording for is played from
 * disk; a line it does not is spoken by the browser voice. That is what makes the pipeline
 * incremental — a script can be half-baked and a child still hears every word — and it is also the
 * honest response to staleness: a reworded line has a different key (see `baked-line-id.ts`), so it
 * has no recording, so it falls back rather than saying the old wording.
 *
 * **Two engines, one mouth.** Speech must never overlap, and a queue that only knew about one of
 * the two engines would let a recording play under a spoken hint. So the queue lives here, one line
 * at a time, and interrupting stops whichever engine is talking.
 *
 * **A recording that never ends may cost silence, never a mission** (the S0-R1 invariant family,
 * D-095(6)). The browser-TTS watchdog's whole reason for existing — an engine that accepts a line
 * and then says nothing about it, ever, wedging every sentence behind it for the rest of the
 * sitting — applies unchanged to a media file that will not load or an element that never fires
 * `ended`. So the baked path has a bound of its own, derived from the line's own length, and the
 * fallback path keeps the watchdog it always had, untouched, inside the service it belongs to.
 */

/**
 * The slice of the sound seam a spoken line needs.
 *
 * Narrow on purpose: this service may know whether a recording exists, start it, and stop it. It
 * may not mute a family's music, claim the foreground, or touch the browser's audio stack — the
 * last of those is an architecture pin, and the first two belong to the wiring point.
 */
export interface BakedLineAudio {
  /**
   * Whether this sitting can make a sound at all.
   *
   * Read on every line, not once at construction: sound is locked until a grown-up's gesture, and
   * a context that refused to resume leaves it locked for the whole sitting. A recording that
   * cannot be played is not a recording — the line goes to the browser voice instead of going
   * silently missing.
   */
  readonly unlocked: boolean;
  declaredIds(): readonly AudioId[];
  /**
   * Play the recording and resolve with **how it ended** (R1-3).
   *
   * The end reason is the difference between a line the child heard and a line the browser refused
   * to play. Without it a refusal is indistinguishable from a recording, and the line goes silently
   * missing with a browser voice sitting right there that could have said it.
   */
  playToEnd(audioId: AudioId, options?: { gain?: number }): Promise<AudioPlaybackEnd>;
  stop(audioId: AudioId): void;
}

/** A narration service that also reports whether the device has a voice at all. */
type FallbackNarrationService = NarrationService & { readonly available?: boolean };

export interface PrebakedNarrationServiceOptions {
  audio: BakedLineAudio;
  /** Said by the browser voice: every line this theme has no recording for. */
  fallback: FallbackNarrationService;
}

interface QueuedLine {
  text: string;
  options: NarrationOptions | undefined;
  settle: () => void;
}

/** The prefix every baked line ID carries (PS5-6). */
const NARRATION_ID_PREFIX = 'narration.';

/**
 * How long this recording is allowed to take before it is presumed lost.
 *
 * Derived from the line's own length at the flagged per-character figure, plus a fixed allowance
 * for the element to load and start. It is not a duration estimate to be tight about: the only
 * thing it must never do is fire while a recording is still being heard.
 */
function bakedLineBoundMs(text: string): number {
  return (
    NARRATION_BOUNDS.bakedLineStartGraceMs + text.length * NARRATION_BOUNDS.bakedLineMsPerCharacter
  );
}

export class PrebakedNarrationService implements NarrationService {
  readonly #audio: BakedLineAudio;
  readonly #fallback: FallbackNarrationService;
  readonly #queue: QueuedLine[] = [];

  #enabled = true;
  #volume = 1;
  #speaking = false;
  /** Cuts the line in flight short, whichever engine is saying it. Null when nothing is. */
  #cancelActive: (() => void) | null = null;
  #last: { text: string; options: NarrationOptions | undefined } | null = null;

  constructor(options: PrebakedNarrationServiceOptions) {
    this.#audio = options.audio;
    this.#fallback = options.fallback;
  }

  /**
   * Whether this device can say anything at all.
   *
   * True when the browser has a voice **or** this sitting has recordings: a machine with no speech
   * synthesis installed is no longer a machine with no narration, and a setup screen that said
   * otherwise would be describing the device rather than the sitting.
   */
  get available(): boolean {
    return (this.#fallback.available ?? false) || this.#hasBakedLines();
  }

  get enabled(): boolean {
    return this.#enabled;
  }

  setEnabled(enabled: boolean): void {
    this.#enabled = enabled;
    this.#fallback.setEnabled(enabled);
    if (!enabled) this.stop();
  }

  setVolume(volume: number): void {
    this.#volume = Number.isFinite(volume) ? Math.min(1, Math.max(0, volume)) : 1;
    this.#fallback.setVolume(volume);
  }

  setVoiceProfile(profile: string): void {
    this.#fallback.setVoiceProfile(profile);
  }

  async speak(text: string, options?: NarrationOptions): Promise<void> {
    this.#last = { text, options };

    if (!this.#enabled || narrationLineKey(text).length === 0) return;

    // Default to interrupting: two voices talking over each other is never acceptable.
    if (options?.interrupt !== false) this.#cancelQueue();

    await new Promise<void>((resolve) => {
      this.#queue.push({ text, options, settle: resolve });
      void this.#drain();
    });
  }

  stop(): void {
    this.#cancelQueue();
  }

  /**
   * Says the last line again, through whichever engine has it.
   *
   * Routed rather than delegated: the fallback's own `replayLast` knows only the lines *it* said,
   * so a baked line repeated through it would be synthesised — the one thing this service exists to
   * prevent. Asking again by text lands on the same recording.
   */
  async replayLast(): Promise<void> {
    if (this.#last === null) return;
    await this.speak(this.#last.text, { ...this.#last.options, interrupt: true });
  }

  #hasBakedLines(): boolean {
    return this.#audio.declaredIds().some((id) => id.startsWith(NARRATION_ID_PREFIX));
  }

  #recordingFor(text: string): AudioId | null {
    if (!this.#audio.unlocked) return null;
    const audioId = bakedNarrationAudioId(text);
    if (audioId === null) return null;
    return this.#audio.declaredIds().includes(audioId) ? audioId : null;
  }

  #cancelQueue(): void {
    const pending = this.#queue.splice(0, this.#queue.length);
    for (const item of pending) item.settle();
    this.#cancelActive?.();
  }

  async #drain(): Promise<void> {
    if (this.#speaking) return;
    this.#speaking = true;
    try {
      for (;;) {
        const next = this.#queue.shift();
        if (next === undefined) break;
        await this.#say(next);
      }
    } finally {
      this.#speaking = false;
    }
  }

  async #say(line: QueuedLine): Promise<void> {
    const audioId = this.#recordingFor(line.text);
    try {
      if (audioId === null) {
        await this.#sayThroughVoice(line.text, line.options);
        return;
      }
      /**
       * A refused recording is an unbaked line (R1-3).
       *
       * The routing above asks whether this sitting can make a sound and whether the theme has the
       * file, and both can be true while the browser still refuses to play it. Falling back here —
       * and *only* on a refusal — is what keeps the per-line promise honest: `stopped` means
       * somebody cut the line short on purpose, and repeating it through the browser voice would
       * talk over whatever interrupted it.
       */
      if ((await this.#playRecording(audioId, line.text)) === 'refused') {
        await this.#sayThroughVoice(line.text, line.options);
      }
    } finally {
      this.#cancelActive = null;
      line.settle();
    }
  }

  async #sayThroughVoice(text: string, options: NarrationOptions | undefined): Promise<void> {
    /**
     * Interrupting is this service's job, and it has already been done.
     *
     * The queue above serialises both engines, so the fallback never has more than one line at a
     * time and never needs to cancel one of its own. `interrupt: true` is passed so that a
     * lingering utterance from before this service took over is cleared rather than queued behind.
     */
    this.#cancelActive = () => {
      this.#fallback.stop();
    };
    await this.#fallback.speak(text, { ...options, interrupt: true });
  }

  async #playRecording(audioId: AudioId, text: string): Promise<AudioPlaybackEnd> {
    let watchdog: ReturnType<typeof setTimeout> | null = null;
    const release = (): void => {
      if (watchdog !== null) clearTimeout(watchdog);
      watchdog = null;
      // Stopping a sound that has already ended is a no-op at the seam below, so this is safe on
      // every path out — including the ordinary one.
      this.#audio.stop(audioId);
    };

    this.#cancelActive = release;
    watchdog = setTimeout(release, bakedLineBoundMs(text));

    try {
      return await this.#audio.playToEnd(audioId, { gain: this.#volume });
    } finally {
      release();
    }
  }
}

/** Convenience factory matching the other platform services. */
export function createPrebakedNarrationService(
  options: PrebakedNarrationServiceOptions,
): PrebakedNarrationService {
  return new PrebakedNarrationService(options);
}
